export interface CompoundInterestInput {
  initialCapital: number;
  monthlyContribution: number;
  /** Angenommene, konstante jährliche Rendite als Dezimalzahl (0,07 = 7 %). */
  annualReturnRate: number;
  years: number;
}

export interface CompoundInterestYearPoint {
  year: number;
  totalContributions: number;
  balance: number;
}

export interface CompoundInterestResult {
  points: CompoundInterestYearPoint[];
  finalBalance: number;
  totalContributions: number;
  totalInterest: number;
}

/**
 * Rein hypothetischer Zinseszins-Rechner (backlog.md -> Aktienfinder-Benchmark,
 * "Zinseszins-Rechner ... rein hypothetisch, aktien-unabhängig") - anders als der
 * bestehende Sparplan-Rechner (lib/valuation/fair-value.ts, echte historische Performance
 * je Aktie inkl. reinvestierter Dividenden) rechnet dieser NUR mit einer angenommenen,
 * konstanten Rendite für Bildungszwecke (FIRE-/Sparquoten-Kontext auf Akademie/
 * Startseite), keine echte Kurshistorie/kein Ticker-Bezug. Monatliche Verzinsung +
 * -Einzahlung für realistischere Zwischenwerte als reine Jahresendverzinsung. Reine
 * Funktion, kein I/O.
 */
export function computeCompoundInterest(input: CompoundInterestInput): CompoundInterestResult | undefined {
  const { initialCapital, monthlyContribution, annualReturnRate, years } = input;
  if (
    !Number.isFinite(initialCapital) ||
    !Number.isFinite(monthlyContribution) ||
    !Number.isFinite(annualReturnRate) ||
    initialCapital < 0 ||
    monthlyContribution < 0 ||
    !Number.isInteger(years) ||
    years < 1 ||
    years > 60
  ) {
    return undefined;
  }

  const monthlyRate = annualReturnRate / 12;
  const points: CompoundInterestYearPoint[] = [
    { year: 0, totalContributions: initialCapital, balance: initialCapital },
  ];
  let balance = initialCapital;
  let totalContributions = initialCapital;

  for (let month = 1; month <= years * 12; month++) {
    balance = balance * (1 + monthlyRate) + monthlyContribution;
    totalContributions += monthlyContribution;
    if (month % 12 === 0) {
      points.push({ year: month / 12, totalContributions, balance });
    }
  }

  return { points, finalBalance: balance, totalContributions, totalInterest: balance - totalContributions };
}
